import { fetchEventUnreadMap } from '../eventlist/eventUnreadUtils';

const normalizeText = (value) => String(value || '').trim();

export const buildEventSearchDraft = ({
  eventQuery = '',
  messageQuery = '',
  startDate = '',
  endDate = '',
  assignee = '',
} = {}) => ({
  eventQuery: normalizeText(eventQuery),
  messageQuery: normalizeText(messageQuery),
  startDate: normalizeText(startDate),
  endDate: normalizeText(endDate),
  assignee: normalizeText(assignee),
});

export const resolveEventSearchCriteria = ({
  override = null,
  eventQuery = '',
  messageQuery = '',
  startDate = '',
  endDate = '',
} = {}) => {
  const source = override && typeof override === 'object' ? override : {};
  const q = normalizeText(source.eventQuery ?? eventQuery);
  const message = normalizeText(source.messageQuery ?? messageQuery);
  const start = normalizeText(source.startDate ?? startDate);
  const end = normalizeText(source.endDate ?? endDate);
  return {
    q,
    message,
    startDate: start,
    endDate: end,
    hasAny: Boolean(q || message || start || end),
  };
};

export const countActiveEventSearchFilters = ({
  eventQuery = '',
  messageQuery = '',
  startDate = '',
  endDate = '',
} = {}) =>
  [
    normalizeText(eventQuery),
    normalizeText(messageQuery),
    normalizeText(startDate),
    normalizeText(endDate),
  ].filter(Boolean).length;

export const loadEventSearchRowsWithUnread = async ({
  searchUrl,
  fetchJsonSafe,
  searchErrorLabel = '',
  apiUrl,
  role,
  unreadLabel = '',
} = {}) => {
  if (typeof fetchJsonSafe !== 'function') {
    return { rawEvents: [], unreadMap: {}, hasMore: false, nextOffset: 0, snapshotToken: '' };
  }
  const payload = await fetchJsonSafe(
    searchUrl,
    {},
    { label: searchErrorLabel }
  );
  const rawEvents = Array.isArray(payload?.events) ? payload.events : [];
  const eventIds = rawEvents
    .map((eventRow) => eventRow?.event_uuid)
    .filter(Boolean);
  const unreadMap = await fetchEventUnreadMap({
    apiUrl,
    role,
    eventIds,
    fetchJsonSafe,
    label: unreadLabel,
  });
  return {
    rawEvents,
    unreadMap: unreadMap && typeof unreadMap === 'object' ? unreadMap : {},
    hasMore: payload?.hasMore === true,
    nextOffset: Number(payload?.nextOffset) || 0,
    snapshotToken: String(payload?.snapshot_token || '').trim(),
  };
};

export const openEventTargetSearchModal = ({
  setErrorMessage,
  setSearchDraft,
  setShowSearchModal,
  currentSearch = {},
} = {}) => {
  if (typeof setErrorMessage === 'function') setErrorMessage('');
  if (typeof setSearchDraft === 'function') {
    setSearchDraft(
      buildEventSearchDraft({
        eventQuery: currentSearch.eventQuery,
        messageQuery: currentSearch.messageQuery,
        startDate: currentSearch.startDate,
        endDate: currentSearch.endDate,
        assignee: currentSearch.assignee,
      })
    );
  }
  if (typeof setShowSearchModal === 'function') setShowSearchModal(true);
};

export const clearEventTargetSearch = async ({
  setErrorMessage,
  setIsSearchMode,
  setSearchQuery,
  setMessageSearchQuery,
  setSearchStartDate,
  setSearchEndDate,
  setHasMoreEvents,
  setEventsOffset,
  reloadDefaultList,
  onCleared,
} = {}) => {
  if (typeof setErrorMessage === 'function') setErrorMessage('');
  if (typeof setIsSearchMode === 'function') setIsSearchMode(false);
  if (typeof setSearchQuery === 'function') setSearchQuery('');
  if (typeof setMessageSearchQuery === 'function') setMessageSearchQuery('');
  if (typeof setSearchStartDate === 'function') setSearchStartDate('');
  if (typeof setSearchEndDate === 'function') setSearchEndDate('');
  if (typeof setHasMoreEvents === 'function') setHasMoreEvents(true);
  if (typeof setEventsOffset === 'function') setEventsOffset(0);
  if (typeof reloadDefaultList === 'function') {
    await reloadDefaultList();
  }
  if (typeof onCleared === 'function') onCleared();
};

export const applyEventTargetSearchDraft = async ({
  searchDraft = {},
  setSearchQuery,
  setMessageSearchQuery,
  setSearchStartDate,
  setSearchEndDate,
  performSearch,
  setShowSearchModal,
  setErrorMessage,
} = {}) => {
  const criteria = resolveEventSearchCriteria({
    override: searchDraft,
  });
  if (typeof setSearchQuery === 'function') setSearchQuery(criteria.q);
  if (typeof setMessageSearchQuery === 'function') setMessageSearchQuery(criteria.message);
  if (typeof setSearchStartDate === 'function') setSearchStartDate(criteria.startDate);
  if (typeof setSearchEndDate === 'function') setSearchEndDate(criteria.endDate);

  if (typeof performSearch !== 'function') return false;
  const ok = await performSearch({
    eventQuery: criteria.q,
    messageQuery: criteria.message,
    startDate: criteria.startDate,
    endDate: criteria.endDate,
  });
  if (ok) {
    if (typeof setShowSearchModal === 'function') setShowSearchModal(false);
    if (typeof setErrorMessage === 'function') setErrorMessage('');
  }
  return ok;
};
